/**
 * Shop-specific styles extending the existing design system
 */
import { COLORS, FONT_FAMILY } from '../styles';

// Extended color palette for shop
export const SHOP_COLORS = {
  ...COLORS,
  // Accent colors for shop actions
  cartGreen: '#22c55e',
  cartGreenHover: '#16a34a',
  priceTag: '#f59e0b',
  saleBadge: '#ef4444',
  // Subtle backgrounds
  cardBg: 'rgba(255,255,255,0.95)',
  cardBgHover: 'rgba(255,255,255,1)',
  overlayDark: 'rgba(0,0,0,0.6)',
  // Filter/category pills
  pillBg: 'rgba(255,255,255,0.2)',
  pillBgActive: COLORS.accent,
};

// Shop-specific spacing
export const SHOP_SPACING = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
};

// Card styles
export const CARD_STYLES = {
  borderRadius: '16px',
  shadow: '0 4px 20px rgba(0,0,0,0.1)',
  shadowHover: '0 8px 30px rgba(0,0,0,0.15)',
  transition: 'all 0.3s ease',
};

// Button variants for shop
export const SHOP_BUTTONS = {
  primary: {
    background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.pink} 100%)`,
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  secondary: {
    background: 'rgba(255,255,255,0.2)',
    color: '#fff',
    border: '2px solid rgba(255,255,255,0.3)',
    borderRadius: '12px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  cart: {
    background: SHOP_COLORS.cartGreen,
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 28px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: FONT_FAMILY,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
};

// Entry card styles for the landing page
export const ENTRY_CARD_STYLES = {
  container: {
    background: SHOP_COLORS.cardBg,
    borderRadius: CARD_STYLES.borderRadius,
    padding: SHOP_SPACING.xl,
    boxShadow: CARD_STYLES.shadow,
    transition: CARD_STYLES.transition,
    cursor: 'pointer',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: SHOP_SPACING.md,
  },
  icon: {
    fontSize: '48px',
    marginBottom: SHOP_SPACING.sm,
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: COLORS.text,
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
  description: {
    fontSize: '16px',
    color: COLORS.textLight,
    fontFamily: FONT_FAMILY,
    margin: 0,
    lineHeight: 1.5,
  },
};

// Product card styles
export const PRODUCT_CARD_STYLES = {
  container: {
    background: SHOP_COLORS.cardBg,
    borderRadius: CARD_STYLES.borderRadius,
    overflow: 'hidden',
    boxShadow: CARD_STYLES.shadow,
    transition: CARD_STYLES.transition,
    cursor: 'pointer',
  },
  image: {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'cover',
  },
  content: {
    padding: SHOP_SPACING.md,
  },
  name: {
    fontSize: '18px',
    fontWeight: '600',
    color: COLORS.text,
    fontFamily: FONT_FAMILY,
    margin: 0,
    marginBottom: SHOP_SPACING.xs,
  },
  price: {
    fontSize: '20px',
    fontWeight: '700',
    color: SHOP_COLORS.priceTag,
    fontFamily: FONT_FAMILY,
    margin: 0,
  },
};

export { COLORS, FONT_FAMILY };

/**
 * Global CSS for fun button hover effects
 * Inject this once in the app root to enable .fun-button class everywhere
 */
export const GLOBAL_FUN_BUTTON_CSS = `
  .fun-button {
    transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
    position: relative;
    overflow: hidden;
  }
  .fun-button:hover {
    transform: translateY(-3px) scale(1.02) !important;
    box-shadow: 0 8px 25px rgba(147, 51, 234, 0.35) !important;
  }
  .fun-button:active {
    transform: translateY(-1px) scale(1.01) !important;
  }
  .fun-button::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s ease;
    pointer-events: none;
  }
  .fun-button:hover::after {
    left: 100%;
  }
  
  /* Focus visible outline for accessibility */
  .fun-button:focus-visible {
    outline: 3px solid #7c3aed;
    outline-offset: 2px;
  }
  
  /* Remove default focus ring but keep for keyboard nav */
  .fun-button:focus:not(:focus-visible) {
    outline: none;
  }
`;
